<template>
    <div class="dashboard">
        <div v-if="doctor">
            <div class="container">
                <div class="columns">
                    <div class="column is-3">
                        <app-sidebar></app-sidebar>
                        <onboarding></onboarding>
                    </div>
                    <div class="column is-9">
                        <div v-for="office in offices" class="mt-7" v-show="office.id == currentOffice">
                            <div class="tabs">
                                <ul>
                                    <li v-for="date in dates" :class="{'is-active': date == currentDate}">
                                        <a @click.prevent="selectDate(date)">{{ date | momentFormat('YYYY-MM-DD', $t('date_selector_format')) }}</a>
                                    </li>
                                </ul>
                            </div>

                            <div class="columns">
                                <div class="column is-6">
                                    <nav class="panel">
                                        <p class="panel-heading">{{ $t('next_appointments_section_title') }}</p>
                                        <div class="panel-block p-0">
                                            <table class="table is-fullwidth is-striped mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>{{ $t('next_appointments_table_details_header') }}</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="availability in officeReservedAvailabilities">
                                                        <td>
                                                            <figure class="image thumbnail is-32x32 is-inline-block is-pulled-left m-1 mr-2 clickable" v-if="hasAvailabilityPictures(availability)" @click.prevent="showEmergency(availability)">
                                                                <img :src="getAvailabilityPicture(availability)" alt="Image Preview" style="max-height: 100%; max-width: 100%;">
                                                            </figure>
                                                            <button class="button is-info is-outline is-pulled-left mr-2" @click.prevent="showEmergency(availability)" v-else>
                                                                <b-icon icon="clipboard-account"></b-icon>
                                                            </button>
                                                            <span class="data-title">
                                                                {{ availability.start }} - {{ availability.end }}
                                                                <span class="is-size-7 has-text-grey has-text-weight-light" v-text="getAvailabilityStatus(availability)"></span>
                                                            </span>
                                                            <span class="data-value" v-text="getPatientInfo(availability)"></span>
                                                        </td>
                                                        <td width="105px" class="has-text-right">
                                                            <button class="button is-danger" :disabled="availability.appointmentId" @click.prevent="cancelInvitation(availability.appointmentInvitationId)">
                                                                <b-icon icon="delete-forever"></b-icon>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    <tr v-if="Object.keys(officeReservedAvailabilities).length == 0">
                                                        <td colspan="2" class="has-text-centered has-text-grey">
                                                            {{ $t('next_appointments_section_empty_placeholder') }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </nav>
                                </div>
                                <div class="column is-6">
                                    <nav class="panel">
                                        <p class="panel-heading">{{ $t('next_availabilities_section_title') }}</p>
                                        <div class="panel-block p-0">
                                            <table class="table is-fullwidth is-striped mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>{{ $t('next_availabilities_table_details_header') }}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="availability in officeAvailabilities">
                                                        <td>
                                                            <button class="button is-info is-outline is-pulled-left mr-2" disabled>
                                                                <b-icon icon="clipboard-account"></b-icon>
                                                            </button>
                                                            <span class="data-title">{{ availability.start }} - {{ availability.end }}</span>
                                                            <span class="data-value">—</span>
                                                        </td>
                                                    </tr>
                                                    <tr v-if="Object.keys(officeAvailabilities).length == 0">
                                                        <td class="has-text-centered has-text-grey">
                                                            {{ $t('next_availabilities_section_empty_placeholder') }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <emergency-modal
            :active="showEmergencyModal"
            v-if="Object.keys(currentEmergency).length > 0"
            @close="closeEmergencyModal()"
            :emergency="currentEmergency"
            :availability="currentAvailability">
        </emergency-modal>
    </div>
</template>

<script>
import { store } from '@/store'
import helpers from '@/mixins/helpers'
import moment from 'moment'

import timeSlotRepository from '@/data/TimeSlotRepository'
import appointmentRepository from '@/data/AppointmentRepository'
import appointmentInvitationRepository from '@/data/AppointmentInvitationRepository'
import emergencyRepository from '@/data/EmergencyRepository'

import AppSidebar from '@/components/Doctor/AppSidebar.vue'
import EmergencyModal from '@/components/Doctor/EmergencyModal.vue'

import Onboarding from '@/components/Doctor/Onboarding.vue'

export default {
    name: 'dashboard',

    components: { AppSidebar, EmergencyModal, Onboarding },

    mixins: [helpers],

    data () {
        return {
            officeReservedAvailabilities: {},
            officeAvailabilities: {},
            officeAppointments: {},
            officeAppointmentInvitations: {},
            dates: [],
            showEmergencyModal: false,
            currentAvailability: {},
            currentEmergency: {}
        }
    },

    mounted () {
        this.setupDates()
        this.refresh()
    },

    methods: {
        refresh() {
            this.refreshOfficeAppointments()
            this.refreshOfficeAvailabilities()
        },

        setupDates() {
            let futureDays = 4

            for (var i = 0; i < futureDays; i++) {
                var date = moment().add(i, 'days').startOf('day').format('YYYY-MM-DD')
                this.dates.push(date)

                if (!this.currentDate && i == 0) {
                    this.selectDate(date)
                }
            }
        },

        selectDate (date) {
            this.setCurrentDate(date)
        },

        refreshOfficeAppointments () {
            if (! this.doctor) {
                return false
            }

            // Load Appointments & Invitations
            this.setLoading(true)

            appointmentInvitationRepository.allForOfficeDate(this.doctor.id, this.currentOffice, this.currentDate, (appointmentInvitations) => {
                this.officeAppointmentInvitations = {}

                _.forEach(appointmentInvitations, (appointmentInvitation) => {
                    this.officeAppointmentInvitations[appointmentInvitation.id] = appointmentInvitation
                })

                this.setLoading(false)
            }, () => {
                this.alertError(this.$t('appointment_invitations_could_not_be_loaded'))

                this.setLoading(false)
            })

            appointmentRepository.allForOfficeDate(this.doctor.id, this.currentOffice, this.currentDate, (appointments) => {
                this.officeAppointments = {}

                _.forEach(appointments, (appointment) => {
                    this.officeAppointments[appointment.id] = appointment
                })

                this.setLoading(false)
            }, () => {
                this.alertError(this.$t('appointments_could_not_be_loaded'))

                this.setLoading(false)
            })
        },

        refreshOfficeAvailabilities () {
            if (! this.doctor) {
                return false
            }

            this.setLoading(true)

            timeSlotRepository.allForDate(this.doctor.id, this.currentOffice, this.currentDate, (timeSlots) => {
                this.officeAvailabilities = {}
                this.officeReservedAvailabilities = {}

                _.forEach(timeSlots, (timeSlot) => {
                    var start = moment.duration(timeSlot.time * 15, 'minutes')
                    var end = start.clone().add(15, 'minutes')

                    var officeTimeSlot = Object.assign(timeSlot, {
                        start: moment.utc(start.asMilliseconds()).format(this.$t('time_format')),
                        end: moment.utc(end.asMilliseconds()).format(this.$t('time_format'))
                    })

                    // Ignore timeSlots of today that are already past
                    if (officeTimeSlot.date == moment().format('YYYY-MM-DD')
                        && moment().format('HH:mm') > moment.utc(start.asMilliseconds()).format('HH:mm')) {
                        return
                    }

                    if (timeSlot.appointmentId || timeSlot.appointmentInvitationId) {
                        this.officeReservedAvailabilities[timeSlot.time] = officeTimeSlot
                    } else {
                        this.officeAvailabilities[timeSlot.time] = officeTimeSlot
                    }
                })

                this.setLoading(false)
            }, () => {
                this.alertError(this.$t('timeslots_could_not_be_loaded'))

                this.setLoading(false)
            })
        },

        cancelInvitation (invitationId) {
            this.setLoading(true)

            var appointmentInvitation = this.officeAppointmentInvitations[invitationId]

            appointmentInvitationRepository.cancel(appointmentInvitation, () => {
                this.refresh()
            }, (error) => {
                this.setLoading(false)
                this.alertError(error.message)
            })
        },

        closeEmergencyModal() {
            this.showEmergencyModal = false
            this.currentEmergency = {}
            this.currentAvailability = {}
            document.documentElement.classList.toggle('is-clipped', false)
        },

        showEmergency(availability) {
            this.setLoading(true)
            var appointment = this.getAppointmentByAvailability(availability)

            var emergencyId = appointment.emergencyId

            emergencyRepository.find(emergencyId, (emergency) => {
                this.currentAvailability = availability
                this.currentEmergency = emergency
                this.showEmergencyModal = true

                this.setLoading(false)
            }, (error) => {
                this.alertError(error.message)

                this.setLoading(false)
            })
        },

        getAppointmentByAvailability(availability) {
            var appointment = {}

            if (availability.appointmentId) {
                appointment = this.officeAppointments[availability.appointmentId]
            } else if (availability.appointmentInvitationId) {
                appointment = this.officeAppointmentInvitations[availability.appointmentInvitationId]
            }

            return appointment
        },

        hasAvailabilityPictures (availability) {
            var appointment = this.getAppointmentByAvailability(availability)

            return appointment && appointment.emergencyPictures.length > 0
        },

        getAvailabilityPicture (availability) {
            var appointment = this.getAppointmentByAvailability(availability)

            return appointment.emergencyPictures[0]
        },

        getAvailabilityStatus (availability) {
            var appointment = {}

            if (availability.appointmentId) {
                appointment = this.officeAppointments[availability.appointmentId]

                return "(" + this.$t('status_confirmed_label') + ")"
            } else if (availability.appointmentInvitationId) {
                appointment = this.officeAppointmentInvitations[availability.appointmentInvitationId]

                if (appointment) {
                    if (appointment.hasBeenCancelled || appointment.hasBeenAutoCancelled) {
                        return "(" + this.$t('status_cancelled_label') + ")"
                    }

                    if (appointment.hasBeenDeclined) {
                        return "(" + this.$t('status_declined_label') + ")"
                    }
                }

                return "(" + this.$t('status_pending_label') + ")"
            }

            return ""
        },

        getPatientInfo (availability) {
            var appointment = {}

            if (availability.appointmentId) {
                appointment = this.officeAppointments[availability.appointmentId]
            } else if (availability.appointmentInvitationId) {
                appointment = this.officeAppointmentInvitations[availability.appointmentInvitationId]
            }

            if (appointment && Object.keys(appointment).length) {
                return this.hashPatientInfo(appointment.patientFirstName) + ' ' + this.hashPatientInfo(appointment.patientLastName) + ' (' + appointment.patientAge + ')'
            }

            return "N/A"
        },

        showPicture (picture) {
            this.$modal.open(
                `<p class="image">
                    <img src="${picture}" alt="Image" />
                </p>`
            )
        }
    },

    watch: {
        currentOffice (newCurrentOffice) {
            this.refresh()
        },

        currentDate (newCurrentOffice) {
            this.refresh()
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/Doctor/Dashboard.vue